/**
 * useActiveModule — the module the simplified rail is scoped to.
 *
 * `'all'` means every enabled module (the rail then shows a module tag on each
 * row). Persisted in `users.settings.activeModuleId` so the scope survives a
 * reload, mirrored optimistically into the auth store.
 */
import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getMeQueryKey, updateMeMutation } from '../generated/api/@tanstack/react-query.gen.js';
import { useAuthStore } from '../store/index.js';
import { adaptMutation } from './adapt-mutation.js';

export function useActiveModule(): { activeModuleId: string; setActiveModule: (moduleId: string) => void } {
  const activeModuleId = useAuthStore((s) => s.user?.settings?.activeModuleId ?? 'all');
  const updateSettings = useAuthStore((s) => s.updateSettings);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...adaptMutation(updateMeMutation(), (moduleId: string) => ({ body: { settings: { activeModuleId: moduleId } } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: getMeQueryKey() }),
  });

  const setActiveModule = useCallback(
    (moduleId: string) => {
      updateSettings({ activeModuleId: moduleId });
      mutation.mutate(moduleId);
    },
    [mutation, updateSettings],
  );

  return { activeModuleId, setActiveModule };
}
